import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Download, Loader2 } from "lucide-react";

function lastNDays(n) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - n);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function OpsAdminChecklistReportes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const backTo = user?.role === "corporate_admin" ? "/ops/admin/checklists" : "/ops/checklists";
  const [{ from, to }, setRange] = useState(lastNDays(30));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ops/checklists/reports/summary?date_from=${from}&date_to=${to}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar reporte");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("chizy_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ops/checklists/reports/export.xlsx?date_from=${from}&date_to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `checklists_${from}_${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al exportar");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="checklist-reportes-page">
      <button onClick={() => navigate(backTo)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Reportes de Checklists</h1>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="reportes-from-input" />
          <input type="date" value={to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="reportes-to-input" />
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50" data-testid="reportes-export-btn"><Download className="w-4 h-4" /> Exportar</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-xs text-slate-500 uppercase font-semibold">Cumplimiento general</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="reportes-overall-pct">{data?.overall_percentage?.toFixed(1) ?? 0}%</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-xs text-slate-500 uppercase font-semibold">Checklists ejecutados</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">{data?.runs_count ?? 0}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-xs text-slate-500 uppercase font-semibold">No conformidades top</div>
              <div className="text-3xl font-bold text-red-500 mt-1">{data?.top_no_items?.[0]?.no_count ?? 0}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">Cumplimiento por sucursal y categoría</div>
            <table className="w-full text-sm" data-testid="reportes-by-branch-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Sucursal</th>
                  <th className="text-left px-4 py-2">Categoría</th>
                  <th className="text-right px-4 py-2">Runs</th>
                  <th className="text-right px-4 py-2">% Cumplimiento</th>
                  <th className="text-right px-4 py-2">Completados</th>
                  <th className="text-right px-4 py-2">En progreso</th>
                  <th className="text-right px-4 py-2">Vencidos</th>
                  <th className="text-right px-4 py-2">Abandonados</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r, i) => {
                  const sc = r.status_counts || {};
                  const completedTotal = (sc.completed || 0) + (sc.completed_with_observations || 0);
                  return (
                    <tr key={i} className="border-t border-slate-50" data-testid={`reportes-row-${r.branch_id}-${r.category_code}`}>
                      <td className="px-4 py-2">{r.branch_name}</td>
                      <td className="px-4 py-2 capitalize">{r.category_code}</td>
                      <td className="px-4 py-2 text-right">{r.runs}</td>
                      <td className="px-4 py-2 text-right font-semibold">{r.percentage_complete.toFixed(1)}%</td>
                      <td className="px-4 py-2 text-right text-green-700">{completedTotal}</td>
                      <td className="px-4 py-2 text-right text-yellow-700">{sc.in_progress || 0}</td>
                      <td className={`px-4 py-2 text-right ${sc.expired > 0 ? "text-red-600 font-bold" : "text-slate-500"}`}>{sc.expired || 0}</td>
                      <td className={`px-4 py-2 text-right ${sc.abandoned > 0 ? "text-red-600 font-bold" : "text-slate-500"}`}>{sc.abandoned || 0}</td>
                    </tr>
                  );
                })}
                {(!data?.rows || data.rows.length === 0) && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Sin datos para este período</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">Ítems con más "No" (top problemas)</div>
            <table className="w-full text-sm" data-testid="reportes-top-no-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2">Ítem</th><th className="text-right px-4 py-2">Veces marcado "No"</th></tr></thead>
              <tbody>
                {(data?.top_no_items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-50"><td className="px-4 py-2">{it.label}</td><td className="px-4 py-2 text-right font-semibold text-red-500">{it.no_count}</td></tr>
                ))}
                {(!data?.top_no_items || data.top_no_items.length === 0) && <tr><td colSpan={2} className="text-center py-6 text-slate-400">Sin no-conformidades en este período</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
